import {
  Autocomplete,
  CircularProgress,
  InputAdornment,
  ListItem,
  ListItemIcon,
  ListItemText,
  SxProps,
  TextField,
  TextFieldProps,
  Theme,
  Typography,
} from "@mui/material";
import { FocusEventHandler, ReactNode, useEffect, useMemo, useRef, useState } from "react";

import { ArrayHelper } from "@/common/helpers/array";
import { AutocompleteOptionType, BaseAutocompleteOption } from "@/common/ts/autocomplete";
import { apiClient } from "@/core/api/ApiClient";
import { OneflowTemplateDto } from "@/core/api/generated/v1";

import AppIcon from "../../Icons/AppIcon";

export type ApiEnumAutocompleteOption = BaseAutocompleteOption<OneflowTemplateDto>;

const dtoToOption = (template: OneflowTemplateDto): ApiEnumAutocompleteOption => ({
  id: template.id?.toString() || "",
  title: template.name || template.id?.toString() || "-",
  optionType: AutocompleteOptionType.Normal,
  data: template,
});

interface Props {
  templateId: Nil<number>;
  /** Load data even if the autocomplete is closed.*/
  isPreload?: boolean;
  /** Auto-select option if it's the single option in the list and the input is required. */
  isAutoSelectSingleOption?: boolean;
  fetchTemplates?: () => Promise<OneflowTemplateDto[]>;
  onChange?: (newValue: Nil<OneflowTemplateDto>) => void;

  // autocomplete specific props
  disabled?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  size?: "small" | "medium";
  label?: ReactNode;
  textFieldProps?: TextFieldProps;
  sx?: SxProps<Theme>;
  onBlur?: FocusEventHandler;
}

export function OneflowTemplateAutocomplete({
  templateId,
  disabled,
  required,
  fullWidth,
  size,
  label,
  textFieldProps,
  isPreload = true,
  isAutoSelectSingleOption = true,
  sx,
  fetchTemplates,
  onBlur,
  onChange,
}: Props) {
  const isPreloadedRef = useRef(false);

  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [templates, setTemplates] = useState<OneflowTemplateDto[] | null>(null);
  const [selectedOption, setSelectedOption] = useState<ApiEnumAutocompleteOption | null>(null);
  const [inputValue, setInputValue] = useState("");

  const options = useMemo(() => templates?.map((x) => dtoToOption(x)) || [], [templates]);

  const fetchList = async () => {
    if (ArrayHelper.isNotEmpty(templates)) {
      return;
    }
    setIsLoading(true);
    try {
      const customList = fetchTemplates ? await fetchTemplates() : null;
      const defaultList = !fetchTemplates
        ? (
            await apiClient.oneflowApi.apiV1ProvidersOneflowTemplatesGet({
              nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
            })
          ).data
        : null;
      setTemplates(customList || defaultList);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  // preload
  useEffect(() => {
    if (isPreload && !isPreloadedRef.current) {
      fetchList();
      isPreloadedRef.current = true;
    }
  }, [isPreload, fetchList]);

  useEffect(() => {
    if (!templateId) {
      setSelectedOption(null);
    } else if (templateId && selectedOption?.id !== templateId.toString()) {
      const option = options.find((x) => x.id === templateId.toString());
      setSelectedOption(option || null);
    }
  }, [templateId, selectedOption, options]);

  // handle isAutoSelectSingleOption
  useEffect(() => {
    const singleOption = options.length === 1 ? options[0] : undefined;
    if (
      isAutoSelectSingleOption &&
      required &&
      !isLoading &&
      !templateId &&
      !selectedOption &&
      singleOption
    ) {
      setSelectedOption(singleOption);
      onChange && onChange(singleOption.data);
    }
  }, [isAutoSelectSingleOption, required, isLoading, templateId, selectedOption, options]);

  return (
    <Autocomplete
      sx={{ minWidth: 200, ...sx }}
      disabled={disabled}
      fullWidth={fullWidth}
      size={size}
      open={open}
      onOpen={() => {
        fetchList();
        setOpen(true);
      }}
      onClose={() => setOpen(false)}
      isOptionEqualToValue={(option, value2) => option.id === value2.id}
      getOptionLabel={(option) => option.title.toString()}
      options={options}
      loading={isLoading}
      autoComplete
      includeInputInList
      filterSelectedOptions={false}
      value={selectedOption}
      inputValue={inputValue}
      onBlur={onBlur}
      onChange={(event, newValue) => {
        setSelectedOption(newValue);
        onChange && onChange(newValue?.data);
      }}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      renderInput={(params) => (
        <TextField
          {...textFieldProps}
          {...params}
          label={label || textFieldProps?.label || "Oneflow template"}
          placeholder='Select...'
          fullWidth
          required={required}
          value={inputValue}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <InputAdornment position='end'>
                  {isLoading ? <CircularProgress color='inherit' size={20} /> : null}
                  {params.InputProps.endAdornment}
                  {textFieldProps?.InputProps?.endAdornment}
                </InputAdornment>
              ),
            },
          }}
        />
      )}
      renderOption={(props, option) => {
        return (
          <ListItem {...props} key={option.id}>
            <ListItemIcon>
              <AppIcon of='data' />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography component='div' variant='body1'>
                  {option.title}
                </Typography>
              }
              // secondary={<></>}
            />
          </ListItem>
        );
      }}
    />
  );
}
